import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { Icon } from 'assets/icons';
import GmModal from 'app/shared/modal/modal';
import TopupForm from 'app/member/modules/wallet';
import { SubscriptionPurchaseContext } from 'contexts/checkout/subscription';
import { useTenant } from 'contexts/tenant';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { useWalletLogService } from 'hooks/walletlogs';
import { getRelativeTimeStampRange } from 'app/shared/utils/date';
import styles from './plan.module.css';

// ----- Helpers -----

function getRenewalCopy(renewal_date, status) {
  if (status === 'expired') return { text: 'Subscription expired', urgent: true };
  const days = Math.ceil((new Date(renewal_date) - Date.now()) / (1000 * 60 * 60 * 24));
  if (days <= 3) return { text: `Renews in ${days} day${days === 1 ? '' : 's'}`, urgent: true };
  if (days <= 7) return { text: `Renews in ${days} days`, urgent: false };
  const date_str = new Date(renewal_date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
  return { text: `Renews ${date_str} · ${days} days away`, urgent: false };
}

function getUsageColor(pct) {
  if (pct >= 90) return '#E24B4A';
  if (pct >= 75) return '#BA7517';
  return '#185FA5';
}

function formatCredits(n) {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n > 99_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

function computeThreshold(last_month_usage, floor) {
  if (!last_month_usage || last_month_usage === 0) return null;
  return Math.max(floor, Math.round(last_month_usage * 0.3));
}

function getBalanceState(balance, threshold) {
  if (balance === 0) return 'zero';
  if (threshold === null) return 'healthy';
  if (balance <= threshold) return 'low';
  return 'healthy';
}

// ----- Channel config -----

const CHANNEL_META = {
  email: {
    label: 'Email credits',
    price_label: '₦0.50 per email',
    icon: 'send',
    healthy: { bg: '#E6F1FB', color: '#0C447C' },
    floor: 200
  },
  sms: {
    label: 'SMS credits',
    price_label: '₦7 per message',
    icon: 'sms',
    healthy: { bg: '#EAF3DE', color: '#27500A' },
    floor: 50
  },
  push: {
    label: 'Push credits',
    price_label: '₦1 per notification',
    icon: 'mobile_push',
    healthy: { bg: '#EEEDFE', color: '#3C3489' },
    floor: 50
  }
};

const STATE_ICON_COLORS = {
  low: { bg: '#FAEEDA', color: '#633806' },
  zero: { bg: '#FCEBEB', color: '#9B1C1C' }
};

// ----- Plan Card -----

export const PlanCard = () => {
  const { fetchSubscription } = useSubscriptionService();
  const { is_agency_managed } = useTenant();
  const { openModal: openSubscriptionModal, setModuleCode } = useContext(
    SubscriptionPurchaseContext
  );

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleUpgrade = () => {
    setModuleCode('campaign');
    openSubscriptionModal();
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { subscriptions } = await fetchSubscription({
        query_string: 'plan_code=campaign&status=active'
      });
      setSub(subscriptions?.[0] ?? null);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handleFocus = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [load]);

  const status = sub?.status ?? 'none';
  const is_active = status === 'active' || status === 'expiring_soon';
  const email_used = sub?.resources?.usage ?? 0;
  const email_allowance = sub?.resources?.limit ?? 0;
  const usage_pct = email_allowance > 0 ? (email_used / email_allowance) * 100 : 0;
  const usage_color = getUsageColor(usage_pct);
  const remaining = email_allowance - email_used;
  const renewal = sub?.expires_at ? getRenewalCopy(sub.expires_at, status) : null;
  const renewal_period = sub?.renewal_period === 'year' ? '/yr' : '/mo';

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>Plan</span>
      </div>

      <div className={styles.cardBody}>
        {loading ? (
          <>
            <Skeleton height={16} width={120} style={{ marginBottom: 6 }} />
            <Skeleton height={12} width={80} style={{ marginBottom: 20 }} />
            <Skeleton height={4} width="100%" borderRadius={2} style={{ marginBottom: 8 }} />
            <Skeleton height={11} width={160} />
          </>
        ) : error ? (
          <p className={styles.errorMsg}>Unable to load — try refreshing.</p>
        ) : !is_active ? (
          <div className={styles.noSubState}>
            <Icon name="credit_card" width={22} height={22} className={styles.noSubIcon} />
            <p className={styles.noSubTitle}>No active plan</p>
            <p className={styles.noSubBody}>
              {is_agency_managed
                ? 'Your plan is managed by your agency.'
                : 'Choose a plan to start sending campaigns to your audience.'}
            </p>
            {!is_agency_managed && (
              <button className={styles.noSubCta} onClick={handleUpgrade} type="button">
                Choose a plan
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.planRow}>
              <span className={styles.planName}>{sub.title}</span>
              {!is_agency_managed && (
                <span className={styles.planPrice}>
                  ₦{(sub?.price?.amount ?? 0).toLocaleString()}
                  <span className={styles.planPeriod}>{renewal_period}</span>
                </span>
              )}
            </div>

            {renewal && (
              <p
                className={styles.renewalText}
                style={{ color: renewal.urgent ? '#E24B4A' : undefined }}
              >
                {renewal.text}
              </p>
            )}

            <div className={styles.usageSection}>
              <div className={styles.usageBar}>
                <div
                  className={styles.usageFill}
                  style={{
                    width: `${Math.min(usage_pct, 100)}%`,
                    backgroundColor: usage_color
                  }}
                />
              </div>
              <p className={styles.usageText}>
                {email_used.toLocaleString()} of {email_allowance.toLocaleString()} used this cycle
                {usage_pct >= 75 && (
                  <span style={{ color: '#BA7517' }}>
                    {' '}
                    · {remaining.toLocaleString()} remaining
                  </span>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ----- Wallet Card -----

const CreditRow = ({ channel, balance, state }) => {
  const meta = CHANNEL_META[channel];
  const icon_colors = state === 'healthy' ? meta.healthy : STATE_ICON_COLORS[state];
  const value_color = state === 'zero' ? '#E24B4A' : state === 'low' ? '#BA7517' : undefined;

  return (
    <div className={styles.creditRow}>
      <div
        className={styles.creditIcon}
        style={{ backgroundColor: icon_colors.bg, color: icon_colors.color }}
      >
        <Icon name={meta.icon} width={14} height={14} />
      </div>
      <span className={styles.creditLabel}>{meta.label}</span>
      <span className={styles.creditValue} style={{ color: value_color }}>
        {formatCredits(balance ?? 0)}
      </span>
    </div>
  );
};

export const WalletCard = () => {
  const { tenant_id } = useSelector((store) => store.user_data);
  const { email, sms, push } = useSelector((state) => state.wallet);
  const { is_agency_managed } = useTenant();
  const { fetchWalletLogs } = useWalletLogService();

  const [thresholds, setThresholds] = useState({ email: null, sms: null, push: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [show_topup, setShowTopup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { last_month } = getRelativeTimeStampRange();
      const base_qs = `tenant_id=${tenant_id}&time_stamp=${last_month.start}~${last_month.end}`;

      const [email_logs, sms_logs, push_logs] = await Promise.all([
        fetchWalletLogs({ query_string: `${base_qs}&resource_type=email` }),
        fetchWalletLogs({ query_string: `${base_qs}&resource_type=sms` }),
        fetchWalletLogs({ query_string: `${base_qs}&resource_type=push` })
      ]);

      const sumUsage = (logs) =>
        (logs.walletlogs ?? []).reduce((s, l) => s + (l.amount ?? l.credits ?? 0), 0);

      setThresholds({
        email: computeThreshold(sumUsage(email_logs), CHANNEL_META.email.floor),
        sms: computeThreshold(sumUsage(sms_logs), CHANNEL_META.sms.floor),
        push: computeThreshold(sumUsage(push_logs), CHANNEL_META.push.floor)
      });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [tenant_id]);

  useEffect(() => {
    load();
    const handleFocus = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [load]);

  const balances = {
    email: email?.balance ?? 0,
    sms: sms?.balance ?? 0,
    push: push?.balance ?? 0
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>Wallet</span>
        {!is_agency_managed && (
          <button
            className={styles.topUpHeaderLink}
            onClick={() => setShowTopup(true)}
            type="button"
          >
            Top up <Icon name="arrow_right" width={12} height={12} />
          </button>
        )}
      </div>

      <div className={styles.cardBody}>
        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={44} style={{ marginBottom: 8 }} borderRadius={8} />
            ))}
          </>
        ) : error ? (
          <p className={styles.errorMsg}>Unable to load — try refreshing.</p>
        ) : (
          <div className={styles.creditsGrid}>
            {['email', 'sms', 'push'].map((ch) => (
              <CreditRow
                key={ch}
                channel={ch}
                balance={balances[ch]}
                state={getBalanceState(balances[ch], thresholds[ch])}
              />
            ))}
          </div>
        )}
      </div>

      <GmModal show_modal={show_topup} onClose={() => setShowTopup(false)}>
        <TopupForm closeModal={() => setShowTopup(false)} />
      </GmModal>
    </div>
  );
};
